import { Controller } from '../utils/controller';
import Cookies from 'js-cookie';

interface ICurrentCustomerPayload {
  current_customer: { first_name: string };
}

enum BackendPlatform {
  Bifrost = 'bifrost',
  Perlgem = 'perlgem'
}

export default class extends Controller {
  readonly API_CUSTOMER_PATH: string = '/api/2024-03/account';
  readonly DEFAULT_ACCOUNT_TEXT: string = 'My Account';
  readonly PERLGEM_COOKIE: string = 'FE_USER_CART';
  readonly userAccountPathValue: string;

  static targets = ['accountLink', 'accountText'];
  static values = {
    userAccountPath: { type: String, default: '/account/index.tmpl' }
  };

  declare accountLinkTargets: HTMLAnchorElement[];
  declare accountTextTargets: HTMLElement[];
  declare backendPlatform: BackendPlatform;

  connect() {
    this.setBackendPlatform();
    this.hydrateCustomerName();
  }

  setBackendPlatform(): void {
    this.backendPlatform = window._be_platform as BackendPlatform;
  }

  /**
   * Identify the backend platform and fetch the customer information.
   * Bifrost: Fetches the customer information from the API.
   * Perlgem: Fetches the customer information from the encoded cookie value.
   */
  async fetchCustomerInformation() {
    if (this.backendPlatform == BackendPlatform.Perlgem) {
      const userCartDataCookie = Cookies.get(this.PERLGEM_COOKIE) || '';
      const cookieDataFormatted = userCartDataCookie.replaceAll(':', '=');
      const userCartDataAsURLParams = new URLSearchParams(cookieDataFormatted);
      const { signed_in, first_name } = Object.fromEntries(userCartDataAsURLParams.entries()) || {};
      const current_customer = signed_in && signed_in === '1' ? { first_name: first_name } : null;
      return current_customer;
    } else {
      const response = await fetch(this.API_CUSTOMER_PATH);
      const { current_customer }: ICurrentCustomerPayload = await response.json();
      return current_customer;
    }
  }

  /**
   * Greet signed-in customers using top-of-funnel navigation menus.
   *
   * We currently have no easy way to instantiate a unified local environment
   * (i.e., that fuses Bifrost and Vulcan). Hence, this feature, which relies on
   * shared data between the two systems, doesn't actually work.
   *
   * To mock the various responses from Bifrost, comment out the following line:
   *
   * ```
   * const { current_customer } = { current_customer: null };
   * ```
   *
   * And replace it with one of the following:
   *
   * ```
   * // Signed out
   * const { current_customer } = { current_customer: null };
   *
   * // Signed in, first name not set
   * const { current_customer } = { current_customer: { first_name: null } };
   *
   * // Signed in, first name set
   * const { current_customer } = { current_customer: { first_name: 'Anne' } };
   * ```
   */
  async hydrateCustomerName() {
    try {
      const current_customer = await this.fetchCustomerInformation();

      // If the customer isn't signed in, update the URLs to include a `return-to`
      // search parameter, and then, exit.
      if (current_customer === null) {
        this.accountLinkTargets.forEach((target) => {
          const url = new URL(target.href);
          url.searchParams.append('return-to', window.location.pathname);
          target.href = `${url.pathname}${url.search}`;
        });

        return;
      }

      const firstName = current_customer.first_name;

      // If the customer is signed in, update the link to point to the account
      // context.
      this.accountLinkTargets.forEach((target) => (target.href = this.userAccountPathValue));

      // If the customer has saved their first name, welcome them, and exit.
      if (firstName) {
        this.accountTextTargets.forEach((target) => (target.innerText = firstName));
        return;
      }

      // In all other cases, use the default text for signed-in customers.
      this.accountTextTargets.forEach((target) => (target.innerText = this.DEFAULT_ACCOUNT_TEXT));
    } catch (err) {
      console.error(err);
    }
  }
}
